/**
 * 人脉面板（业内人脉列表页 + 聊天页 + 经纪人聊天页三视图）
 * 设计原型已归档（2026-10-24）：原基准 docs/页面原型.md §页面5 + 页面5a + 页面5b，ui-concept/人脉.html + 人脉聊天.html；现行实现见本文件 + docs/UI规范.md §2.1/§3
 * 数据源：GameData.NPCS + GameState.relationships（好感度 / 未结识 -1）
 * 交互：列表顶部为置顶经纪人行（薄荷底即置顶语义，无好感度/关系，进入 5b 经纪人聊天页），
 *       余下已结识 NPC（好感 ≥0）按档位排序：有红点 > 特殊人脉 > 普通人脉，同档内按好感度降序，点击整行进入聊天页（5a）；
 *       聊天页顶部 NPC 信息条点击进入详情页（5d，批次 C）：个人信息 + [删除好友]（仅随机 NPC，二次确认），详情返回落聊天页；
 *       聊天页无开场白，「聊天」方块 NPC 从池中随机发一条固定文案（无选项），邀约文案同读 NPC 数据表；
 *       常态动作=一排方块（聊天/邀约/客串/帮宣，2026-11-01 方案 B″），待办与二级剧单仍是通栏整行；
 *       送礼入口已从聊天页删除（2026-11-01 茄子令；原 2026-10-11 自由出行批加的清单行与二级礼物清单一并撤）：
 *       底层 giftTexts 文案池 / Gifts 表 / SocialSystem.gift 保留，供后续重设计或熟遇口复用
 */
import type { GameApp } from '../../app';
import { GameState } from '../../systems/GameState';
import { EventBus, GameEvents } from '../../core/EventBus';
import { socialSystem } from '../../systems/SocialSystem';
import { greetingSystem } from '../../systems/GreetingSystem';
import { guestCameoSystem } from '../../systems/GuestCameoSystem';
import { friendPromoSystem } from '../../systems/FriendPromoSystem';
import { endorsementSystem } from '../../systems/EndorsementSystem';
import { rankSystem } from '../../systems/RankSystem';
import { normalNpcSystem } from '../../systems/NormalNpcSystem';
import { scriptSystem, scheduleDeadlineTextOf } from '../../systems/ScriptSystem';
import { PlotSystem, settledChatEffects } from '../../systems/PlotSystem';
import { GameConfig } from '../../core/GameConfig';
import { GameData } from '../../data/GameData';
import { NormalNpc, NpcGreeting, AwardNominee, AwardRecord, FilmographyEntry } from '../../data/types';
import { AWARD_MAP, absMonthOf, AwardId, findCeremony } from '../../data/tables/Awards';
import { PERSONALITY_TEMPLATES, PersonalityKey } from '../../data/tables/NormalNpcData';

import { createDiv, createSpan, createButton, makeClickable, showHint, showValueChanges, appendChanges, setPlotText, fillLevelBadge, HINT_MS } from '../ui';
import { RoleDetailPop } from '../roleCard';
import { formatBeatEffects } from '../plotStage';
import { icon, IconName } from '../icons';
import { fillAvatarSlot, prefetchAvatar } from '../../avatar/gamemount';
import type { AvatarConfig } from '../../avatar/config';

/** 好感度 → 五档刻度（43 批乙+套一）：档位线 20/40/60/80。未结识仅在数据兜底时出现，正常流程列表已过滤。 */
function tierOf(favor: number): { name: string; cls: string } {
    if (favor < 0) return { name: '未结识', cls: 'low' };
    if (favor >= 80) return { name: '挚友', cls: 'high' };
    if (favor >= 60) return { name: '好友', cls: 'high' };
    if (favor >= 40) return { name: '熟悉', cls: '' };
    if (favor >= 20) return { name: '认识', cls: 'low' };
    return { name: '初识', cls: 'low' };
}

/** 爱心 = 连续比例尺（44 批，用户否掉 43 批「格数=档号」：80 起满格空转）：
 *  一格 20 点，描边 ♥ 底上盖一层按宽度裁切的同字实心 ♥ ⇒ 80 好感 4 实 1 空、100 才满；档位深浅仍读档位词。
 *  底符原为 ♡（批1·问题⑤）：♡/♥ 两字形 advance 不同，% 裁切参考宽永远对不齐，改同字形底+text-stroke 空心。 */
function renderHearts(host: HTMLElement, favor: number): void {
    host.innerHTML = '';
    const v = Math.max(0, favor);
    for (let i = 0; i < 5; i++) {
        const hp = createSpan('hp');
        hp.appendChild(createSpan('off', '♥'));
        const on = createSpan('on', '♥');
        on.style.setProperty('--hp', `${Math.max(0, Math.min(100, (v - i * 20) * 5))}%`);
        hp.appendChild(on);
        host.appendChild(hp);
    }
}

/** 聊天记录条目：消息气泡（客串邀请的选项⛔进流，统一走底部输入条展开的选项清单，9/26 布局改案）
 *  abs = 消息所属绝对月（year*12+month）：渲染时与上一条不同月就在其上方插一行微信式居中日期行
 *  （2026-10-25 茄子令，只显示年月）；同组后续气泡不带 abs，跟着组头走不另插行
 *  choices/pickedChoice/replies = 剧情消息的回复选项与定格态（未答的入口在底部待办清单，不在气泡流里画按钮）
 *  changes = 这一拍改的账挂在气泡下方的变动行（账落过才给：无回复的投递即落，有回复的要等选定——2026-10-15 茄子定） */
type ChatEntry = { type: 'msg'; me: boolean; text: string; /** 存档兼容字段；2026-11-01 起不再渲染，好感走 flashChatHint 临时气泡 */ aff?: number; abs?: number; choices?: string[]; pickedChoice?: number; replies?: (string | undefined)[]; changes?: { label: string; delta: string }[] };

export class CharactersPanel {
    readonly element: HTMLElement;

    private levelBadge: HTMLElement;
    private dateBadge: HTMLElement;
    private nameEl: HTMLElement;
    /** 顶部主角头像槽（67 批：捏脸立绘，📇 兜底） */
    private avatarEl: HTMLElement;
    private npcList: HTMLElement;
    private listBlock: HTMLElement;

    // 聊天页
    private chatRoot: HTMLElement;
    private chatDateBadge: HTMLElement;
    private chatAvatar: HTMLElement;
    private chatName: HTMLElement;
    private chatRole: HTMLElement;
    private chatHearts: HTMLElement;
    private chatAffNum: HTMLElement;
    private chatLog: HTMLElement;
    /** 底部输入条（伪触发：不可打字，点一下才展开选项清单）+ 选项清单容器 */
    private inputBar: HTMLElement;
    private optList: HTMLElement;
    /** 选项清单是否展开 */
    private optOpen = false;
    /** 清单是否处于「邀客串」二级列表（客串批②：整单替换为自己的在拍合格单，选完/取消回默认） */
    private guestInviteMode = false;
    /** 清单是否处于「邀宣传」二级列表（好友剧宣批：整单替换为自己的在播合格单，形制同邀客串） */
    private friendPromoMode = false;

    /** 当前聊天 NPC ID（null=列表模式）；'MANAGER' 表示经纪人聊天页 */
    private currentNpcId: string | null = null;
    /** 当前详情页 NPC ID（批次 C：详情页从聊天页信息条进入，返回落聊天页） */
    private currentDetailId: string | null = null;
    /** 当前会话聊天记录（普通 NPC 用） */
    private log: ChatEntry[] = [];
    /** 聊天流内的临时好感气泡 + 淡出计时器（2026-11-01：不写历史，离开聊天页即清） */
    private chatHint: HTMLElement | null = null;
    private chatHintTimer = 0;

    // 详情页（页面5d · 批次 C：个人信息 + 删除好友，聊天/删除的唯一入口）
    private detailRoot: HTMLElement;
    private detailDateBadge: HTMLElement;
    private detailAvatar: HTMLElement;
    private detailName: HTMLElement;
    private detailSub: HTMLElement;
    private detailHearts: HTMLElement;
    private detailInfoBlock: HTMLElement;
    private detailDeleteBtn: HTMLButtonElement;
    /** 内容滚动区（承载 Hero/个人信息/近期动向） */
    private detailScroll: HTMLElement;
    /** 近期动向薄荷块（无数据显示时整块隐藏） */
    private detailDynamics: HTMLElement;
    /** 近期动向时序是否已展开（默认收 3 条，点「查看全部」展开） */
    private detailDynExpanded = false;
    // 删除好友确认弹窗
    private delOverlay: HTMLElement;
    private delWrap: HTMLElement;
    private delBody: HTMLElement;
    private delName = '';

    // 经纪人聊天页（页面 5b）
    private managerRoot: HTMLElement;
    private managerDateBadge: HTMLElement;
    /** 通知气泡区：经纪人月度通知历史 */
    private managerNoticeList: HTMLElement;
    /** 2026-10-02 用户改案：经纪人页底部 ⛔ 常驻「解约通告」按钮，改挂伪输入条 + 点开的选项清单（与聊天页同款形制） */
    private managerInputBar: HTMLElement;
    private managerOptList: HTMLElement;
    private managerOptOpen = false;
    // 解约弹窗
    private cancelOverlay: HTMLElement;
    private cancelWrap: HTMLElement;
    private cancelBody: HTMLElement;
    private cancelList: HTMLElement;
    // 当期剧本册（2026-10-28 茄子定稿：⛔ 弹层——清单本身当导航，点「当期剧本册」→选项变剧本列表
    // →点剧本变该剧组可试角色→点角色经纪人开剧本详情页；买通稿/危机公关=预留位无弹层）
    private managerBookMode = false;
    private managerBookScript: string | null = null;   // null=剧本列表层，非空=该剧组角色层
    // 直签戏约详情层（公共 RoleDetailPop 组件——与 5c 竞角揭晓详情同一份弹层；点合同块/剧本册角色项打开）
    private directDetail!: RoleDetailPop;

    constructor(private app: GameApp) {
        const panel = createDiv('panel-root');
        const inner = createDiv('characters-inner');

        // 顶部状态栏（与人脉原型一致）
        const topBar = createDiv('top-bar');
        this.avatarEl = createDiv('avatar', ['📇']);
        topBar.appendChild(this.avatarEl);
        const info = createDiv('top-info');
        info.appendChild(createSpan('p-name', '沈星瑶'));
        this.nameEl = info.lastElementChild as HTMLElement;
        this.levelBadge = createSpan('level-badge', '');
        this.dateBadge = createSpan('date-badge', '');
        info.appendChild(this.levelBadge);
        info.appendChild(this.dateBadge);
        topBar.appendChild(info);
        inner.appendChild(topBar);

        // 业内人脉薄荷块（列表滚动，经纪人置顶；65 批：三主页撤撕边+胶带，恢复圆角）
        this.listBlock = createDiv('mint-block list-block');
        this.npcList = createDiv('npc-list');
        this.listBlock.appendChild(this.npcList);
        inner.appendChild(this.listBlock);

        panel.appendChild(inner);
        this.element = panel;

        // 聊天页（覆盖于列表之上）
        this.chatRoot = createDiv('chat-root hidden-chat');
        this.buildChatView();
        panel.appendChild(this.chatRoot);

        // 经纪人聊天页（页面 5b：覆盖于列表之上，与聊天页互斥）
        this.managerRoot = createDiv('manager-chat hidden-chat');
        this.buildManagerView();
        panel.appendChild(this.managerRoot);

        // 详情页（页面 5d：个人信息 + 删除好友，批次 C）
        this.detailRoot = createDiv('detail-root hidden-chat');
        this.buildDetailView();
        panel.appendChild(this.detailRoot);
    }

    /** 构建详情页（页面 5d）静态结构：顶栏返回聊天 + NPC 信息卡 + 个人信息格 + 近期动向 + 底部 [删除好友]（仅随机 NPC） */
    private buildDetailView(): void {
        const head = createDiv('chat-head');
        head.appendChild(createButton('back-btn', '返回聊天', () => this.closeDetail(), 'chevronLeft'));
        this.detailDateBadge = createSpan('date-badge', '');
        head.appendChild(this.detailDateBadge);
        this.detailRoot.appendChild(head);

        // 内容滚动区（近期动向展开后可能超屏；底部删除按钮固定不随滚）
        this.detailScroll = createDiv('detail-scroll');

        // Hero 区：大头像居中 + 姓名 + 职业/身份小字 + 爱心（性格不上屏，只靠对话体现）
        const hero = createDiv('detail-hero');
        this.detailAvatar = createDiv('npc-avatar detail-avatar');
        hero.appendChild(this.detailAvatar);
        this.detailName = createDiv('detail-name');
        hero.appendChild(this.detailName);
        this.detailSub = createDiv('detail-sub');
        hero.appendChild(this.detailSub);
        this.detailHearts = createDiv('npc-hearts');
        hero.appendChild(this.detailHearts);
        this.detailScroll.appendChild(hero);

        // 个人信息薄荷块：咖位/好感/关系（⛔ 不排性格格——性格只用对话体现）
        this.detailInfoBlock = createDiv('detail-info mint-block');
        this.detailScroll.appendChild(this.detailInfoBlock);

        // 近期动向薄荷块（批次：好友详情页动向模块）：当前在拍/在播 + 作品与奖项时序；无数据整块隐藏
        this.detailDynamics = createDiv('detail-dynamics mint-block');
        this.detailScroll.appendChild(this.detailDynamics);

        this.detailRoot.appendChild(this.detailScroll);

        // 底部：[删除好友] 仅随机 NPC（特殊 NPC 无删除语义，方案 §三）；聊天在返回里，不放重复入口
        const actions = createDiv('chat-actions');
        this.detailDeleteBtn = createButton('act-btn act-cancel', '删除好友', () => this.openDeleteConfirm());
        actions.appendChild(this.detailDeleteBtn);
        this.detailRoot.appendChild(actions);

        // ===== 删除确认弹窗（遮罩 + 卡片；挂 app.overlayRoot＝应用外壳，inset:0 才盖得住底部导航条，
        // 挂子视图里页签带照常可点——2026-10-25 治「弹窗开着还能切页签」，与微博页同批） =====
        const overlay = createDiv('dimoverlay');
        this.app.overlayRoot.appendChild(overlay);
        const wrap = createDiv('modal-wrap');
        const body = this.delBody = createDiv('modal-note-card cancel-card');
        const title = createDiv('mtitle', ['删除好友']);
        title.insertBefore(icon('scissors'), title.firstChild);
        body.appendChild(title);
        body.appendChild(createSpan('cancel-tip',
            '删除后好感清零、移出人脉页，之后跑通告仍可能再次偶遇（重遇即重新初遇）。'));
        const mActions = createDiv('modal-actions');
        mActions.appendChild(createButton('btn-cancel', '取消', () => this.closeDeleteConfirm()));
        mActions.appendChild(createButton('btn-post', '删除', () => this.confirmDelete()));
        body.appendChild(mActions);
        wrap.appendChild(body);
        this.app.overlayRoot.appendChild(wrap);
        this.delOverlay = overlay;
        this.delWrap = wrap;
    }

    /** 构建聊天页静态结构 */
    private buildChatView(): void {
        // 顶栏：返回 + 日期徽章（姓名已在下方薄荷条，不再重复）
        const head = createDiv('chat-head');
        const backBtn = createButton('back-btn', '返回人脉', () => this.closeChat(), 'chevronLeft');
        head.appendChild(backBtn);
        this.chatDateBadge = createSpan('date-badge', '');
        head.appendChild(this.chatDateBadge);
        this.chatRoot.appendChild(head);

        // NPC 信息条：薄荷块（批次 C：整条可点 → 详情页，右端 › 提示可进）
        const npcBar = makeClickable(createDiv('chat-npc'), () => this.openDetail(this.currentNpcId!));
        this.chatAvatar = createDiv('npc-avatar');
        npcBar.appendChild(this.chatAvatar);
        const id = createDiv('npc-id');
        this.chatName = createDiv('npc-name');
        this.chatRole = createDiv('npc-role');
        id.appendChild(this.chatName);
        id.appendChild(this.chatRole);
        npcBar.appendChild(id);
        this.chatHearts = createDiv('npc-hearts');
        npcBar.appendChild(this.chatHearts);
        this.chatAffNum = createDiv('chat-aff-num');
        npcBar.appendChild(this.chatAffNum);
        npcBar.appendChild(createSpan('npc-chevron', '›'));
        this.chatRoot.appendChild(npcBar);


        // 聊天记录区（滚动）
        this.chatLog = createDiv('chat-log');
        this.chatRoot.appendChild(this.chatLog);

        // 底部：输入条（伪触发，不可打字）+ 点它才展开的选项清单
        // 形制参考《被囚禁的掌心》聊天页（用户 2026-09-26 给图定稿）：通栏纯文字行 + 发丝分隔线、左对齐、⛔ 卡片钮
        // 清单内容：有未抉择客串邀请时=整单替换为接受/婉拒；否则=回应招呼(N) / 聊天(剩N/3) / 邀约
        this.inputBar = createDiv('chat-input-bar');
        const input = createDiv('chat-fake-input', ['说点什么…']);
        this.inputBar.appendChild(input);
        this.inputBar.appendChild(createButton('chat-send', '发送'));
        makeClickable(this.inputBar, () => this.toggleOptions());
        this.chatRoot.appendChild(this.inputBar);

        this.optList = createDiv('chat-opt-list');
        this.chatRoot.appendChild(this.optList);

        // 开关清单（2026-11-02 茄子令）：**只有**伪输入条或记录区能开/关；其余区域（信息条/顶栏等）
        // 一律不改清单状态。10-03 用户口令「选完 ⛔ 自动收」仍保留（动作行点击不触发本处理器）。
        this.chatRoot.addEventListener('click', e => {
            const t = e.target as Node | null;
            // 选完会整单重建清单：被点的行此时已离树，⛔ 让这跳冒泡冒充「点了别处」
            if (!t || !t.isConnected) return;
            if (this.inputBar.contains(t) || this.optList.contains(t)) return;
            if (this.chatLog === t || this.chatLog.contains(t)) this.toggleOptions();
        });
    }

    /** 构建经纪人聊天页（页面 5b）静态结构：顶栏 + 通知历史列表 + 底部动作方块（2026-11-01 无图标） */
    private buildManagerView(): void {
        const manager = GameData.MANAGER;

        // 顶栏：返回 + 日期徽章（经纪人名已在下方薄荷条）
        const head = createDiv('chat-head');
        const backBtn = createButton('back-btn', '返回人脉', () => this.closeManagerChat(), 'chevronLeft');
        head.appendChild(backBtn);
        this.managerDateBadge = createSpan('date-badge', '');
        head.appendChild(this.managerDateBadge);
        this.managerRoot.appendChild(head);

        // 经纪人信息条：与聊天页 NPC 条同构（无好感度——右侧不摆占位句，2026-10-25 茄子令删）
        const npcBar = createDiv('chat-npc');
        const avatar = createDiv('npc-avatar manager-avatar');
        fillAvatarSlot(avatar, manager.icon, manager.avatar);
        npcBar.appendChild(avatar);
        const id = createDiv('npc-id');
        id.appendChild(createDiv('npc-name', [manager.name]));
        id.appendChild(createDiv('npc-role', [manager.role]));
        npcBar.appendChild(id);
        this.managerRoot.appendChild(npcBar);


        // 通知气泡区：经纪人月度通知历史（最新在上滚动回看）
        this.managerNoticeList = createDiv('manager-notice');
        this.managerRoot.appendChild(this.managerNoticeList);

        // 底部：伪输入条 + 点它才展开的选项清单（2026-10-02 用户改案，形制=聊天页一致；解约通告 从常驻按钮挪进清单）
        this.managerInputBar = createDiv('chat-input-bar');
        this.managerInputBar.appendChild(createDiv('chat-fake-input', ['说点什么…']));
        this.managerInputBar.appendChild(createButton('chat-send', '发送'));
        makeClickable(this.managerInputBar, () => this.toggleManagerOptions());
        this.managerRoot.appendChild(this.managerInputBar);
        this.managerOptList = createDiv('chat-opt-list');
        this.managerRoot.appendChild(this.managerOptList);

        // ===== 解约弹窗（遮罩 + 卡片；挂壳理由同删除确认） =====
        this.cancelOverlay = createDiv('dimoverlay');
        this.app.overlayRoot.appendChild(this.cancelOverlay);
        this.cancelWrap = createDiv('modal-wrap');
        this.cancelBody = createDiv('modal-note-card cancel-card');
        const cancelTitle = createDiv('mtitle', ['解约通告']);
        this.cancelBody.appendChild(cancelTitle);
        this.cancelList = createDiv('cancel-list');
        this.cancelBody.appendChild(this.cancelList);
        const actions = createDiv('modal-actions');
        actions.appendChild(createButton('btn-cancel', '取消', () => this.closeCancelModal()));
        actions.appendChild(createButton('btn-post', '解约', () => this.confirmBreakContract()));
        this.cancelBody.appendChild(actions);
        this.cancelWrap.appendChild(this.cancelBody);
        this.app.overlayRoot.appendChild(this.cancelWrap);

        // ===== 直签戏约详情层（公共 RoleDetailPop 组件，挂壳同解约弹窗；点经纪人消息下的
        //  「合同块」打开——遮罩/卡片/背板关闭/esc/卡体/按钮行骨架与 5c 揭晓详情同一份） =====
        this.directDetail = new RoleDetailPop(this.app.overlayRoot);

        // 开关清单（2026-11-02 茄子令，与聊天页同款）：**只有**伪输入条或通知记录区能开/关；
        // 其余区域一律不改清单状态（弹窗在清单之上另层，也不连带收起）。
        this.managerRoot.addEventListener('click', e => {
            const t = e.target as Node | null;
            if (!t || !t.isConnected) return;
            if (this.managerInputBar.contains(t) || this.managerOptList.contains(t)) return;
            if (this.cancelOverlay.contains(t) || this.cancelWrap.contains(t)) return;
            if (this.managerNoticeList === t || this.managerNoticeList.contains(t)) this.toggleManagerOptions();
        });
    }

    // ===== 聊天页控制 =====

    /**
     * 这一位 NPC 名下「要在这位这里办」的待办（2026-10-15 茄子令：只要收到 message 没回复，进聊天页默认
     * 把底部回复清单摊开）。四类账各一条判据，全库只写在这一个口里——
     *  招呼（随机 NPC 与特殊 NPC 同一本账 `player.greetings`，未 `replied`）、
     *  客串邀请（同账 `kind === 'guestInvite'` 且未 `picked`）、
     *  剧情私信（`plotChats` 挂了 choices 而 `pickedChoice` 还空着）、
     *  赴约选周（选周自动化 2026-10-28 / 多槽 2026-10-29：本 NPC 在 `player.pendingInvites` 里有 `week` 未定的一条）。
     * `renderChatOptions`（列哪几行）与 `openChat`（进页展不展开）共用这把尺，禁止 两处各写一遍。
     */
    private pendingReplies(npcId: string) {
        const state = GameState.getInstance();
        const chats = state.player.plotChats ?? [];
        const chatIdx = chats.findIndex(c => c.npcId === npcId && !!c.choices?.length && c.pickedChoice === undefined);
        const invite = state.player.greetings.find(g => g.npcId === npcId && g.kind === 'guestInvite' && g.picked === undefined);
        const greet = state.player.greetings.find(g => g.npcId === npcId && !g.replied && g.kind !== 'guestInvite');
        const datePick = (state.player.pendingInvites ?? []).some(i => i.npcId === npcId && !i.week);
        return { chatIdx, chat: chatIdx >= 0 ? chats[chatIdx] : undefined, invite, greet, datePick };
    }

    /** 进入聊天页（无开场白，空屏，由 [💬 聊天] 发起第一条；批次 B 起回放该 NPC 的历史招呼气泡，客串批①起含邀请与其定格回话；
     *  2026-10-14 起再回放剧情聊天消息（message 载体的剧情月底统一送达，见 PlotSystem.sweepChatDelivery）） */
    private openChat(npcId: string): void {
        this.currentNpcId = npcId;
        this.rebuildChatLog();
        // 进页即已读：清招呼红点 + 剧情消息红点 + 底部徽章
        if (greetingSystem.markRead(npcId) > 0) EventBus.emit(GameEvents.STATE_CHANGED);
        PlotSystem.getInstance().markChatsRead(npcId);
        this.hideAllChatViews();
        this.listBlock.classList.add('hidden-panel');
        this.chatRoot.classList.remove('hidden-chat');
        this.app.setTabBarVisible(false);
        this.renderChat();
        this.renderChatHeads();
        // 展开态默认值（2026-11-01 茄子令：进页**一律**摊开底部选项栏，不再区分是否待办；
        // 原 2026-10-15 口径是「有待办才摊开、没待办只露输入条」）。玩家手动收起后本次不再自动摊开——
        // 自动只发生在进页那一刻（9/26 布局改案的反向约束仍保留）。
        this.optOpen = true;
        this.guestInviteMode = false;
        this.friendPromoMode = false;
        this.renderChatOptions();
    }

    /** 当前绝对月（year*12+month）：会话内新推气泡落日期行判定用（落盘消息各自的 abs 在 rebuildChatLog 给） */
    private get nowAbs(): number {
        const p = GameState.getInstance().player;
        return p.year * 12 + p.month;
    }

    /**
     * 聊天交换定格记账（2026-10-28 修「读档后记录消失」家族的统一口）：五类交换（聊天/邀客串/
     * 邀宣传/招呼回应/邀约）的气泡全进 `chatMsgs` 账。2026-10-28 存档模型统一：⛔ 当场存档已拆，
     * 记录与好感/次数一起等下一次周收口捎带落盘——周内退出=重玩本周，整笔交换回滚、账自洽。
     * 账落在 `this.log` 之外——rebuildChatLog 重建时从账回放，读档/换人/切聊天对象都不丢。
     * `aff`=存档兼容保留（2026-11-01 起不再渲染，好感改走 `flashChatHint` 的临时气泡）。
     */
    private recordChatMsgs(npcId: string, msgs: { me: boolean; text: string; aff?: number }[]): void {
        const p = GameState.getInstance().player;
        const abs = this.nowAbs;
        // ⛔ 不落盘（存档模型统一 2026-10-28）：记录与账全等下一次周收口捎带落盘
        p.chatMsgs = [...(p.chatMsgs ?? []), ...msgs.map(m => ({ npcId, me: m.me, text: m.text, abs, ...(m.aff ? { aff: m.aff } : {}) }))];
    }

    /**
     * 聊天流内的临时好感通知（2026-11-01 用户令「加好感也用通知气泡，显示在最后一句聊天气泡的
     * 下方一点点、聊天页的中间」）：
     * - 位置＝记录流末尾、最后一条气泡下方 8px（`.chat-log` 的 gap）、水平居中；视觉复用全局 `.hint`；
     * - 行为同 toast：2200ms 后淡出并移除，离开聊天页/换人/重建记录立即清掉 ⇒ **不写历史**；
     * - 同屏只留一枚（新顶旧）；`parts` 可把「名气 +N」「好感 +M」并列进同一枚。
     * - **独立实例**：与全局 `#app-hint`（普通提示/数值变化）互不共用（2026-11-01 用户令），
     *   两者可同屏——一枚在聊天流末尾、一枚在屏幕底部，位置不同不重叠。
     */
    private flashChatHint(parts: string[]): void {
        if (!parts.length) return;
        this.clearChatHint();
        const el = createDiv('hint chat-hint');
        parts.forEach(p => el.appendChild(createSpan(`vc-delta ${/[−-]\s*\d/.test(p) ? 'neg' : 'pos'}`, p)));
        this.chatLog.appendChild(el);
        this.chatHint = el;
        this.chatLog.scrollTop = this.chatLog.scrollHeight;
        this.chatHintTimer = window.setTimeout(() => {
            el.classList.add('hidden');
            this.chatHintTimer = window.setTimeout(() => {
                el.remove();
                if (this.chatHint === el) this.chatHint = null;
            }, 260);                                   // = `.hint` 的 opacity 过渡（--dur-md 0.25s）
        }, HINT_MS);
    }

    /** 清掉聊天流内的临时好感气泡（离开聊天页/换人/重建记录/新顶旧） */
    private clearChatHint(): void {
        if (this.chatHintTimer) { clearTimeout(this.chatHintTimer); this.chatHintTimer = 0; }
        this.chatHint?.remove();
        this.chatHint = null;
    }

    /** 队列 → 会话记录回放：剧情聊天消息 + 招呼/邀请气泡 +（若已抉择）定格回话两口气泡。
     *  未抉择的邀请不在气泡流里出选项，由底部清单整单替换承担（9/26 布局改案）。
     *  回放⛔重掷文案（定格落盘纪律）。
     *  2026-10-14：剧情消息（message 载体月底送达的）与招呼按绝对月统一排序——都是「收到的历史消息」；
     *  招呼组（主气泡+定格回话）作为整体参与排序，⛔ 拆散。 */
    private rebuildChatLog(): void {
        const timed: { abs: number; entries: ChatEntry[] }[] = [];
        const plotChats = PlotSystem.getInstance().chatMessagesFor(this.currentNpcId ?? '');
        // 一条 message 拍按换行拆成多颗气泡（2026-10-15 茄子令「按文档拆成三颗」）；日期行只在组头
        // 第一颗上方插（组内连发不同月也不会有——拆行不跨月），渲染侧见 renderChat 的换月判定
        for (const c of plotChats) {
            // 变动行只在账落过之后才显形：带回复的要等玩家选定（不选＝不加，也就不出行），
            // 不带回复的投递那一刻已落账。清单读的是存根落档那一份（`settledChatEffects`＝这拍的账 + 所选那条的账），
            // 不许反查事件表（表以后改了，老气泡上的变动行不许跟着变）。
            const paid = (c.choices?.length ? c.pickedChoice !== undefined : true) ? settledChatEffects(c) : [];
            timed.push({ abs: c.absMonth, entries: [{ type: 'msg', me: false, text: c.text, abs: c.absMonth, choices: c.choices, pickedChoice: c.pickedChoice, replies: c.replies, changes: paid.length ? formatBeatEffects(paid) : undefined }] });
        }
        for (const g of GameState.getInstance().player.greetings.filter(x => x.npcId === this.currentNpcId)) {
            const abs = g.year * 12 + g.month;
            const entries: ChatEntry[] = [{ type: 'msg', me: false, text: g.text, abs }];
            // 定格回话两段都可能含 // 连发（玩家回复段 / NPC 再回复段），回放时同样拆气泡
            (g.replyTexts ?? []).forEach((t, i) => {
                t.split('//').map(s => s.trim()).filter(Boolean)
                    .forEach(text => entries.push({ type: 'msg', me: i === 0, text }));
            });
            timed.push({ abs, entries });
        }
        // 聊天交换回放（2026-10-28 修「读档后记录消失」家族）：六类交换的气泡定格在 chatMsgs 账里，
        // 读档/换人不丢；定格不重掷（存的就是当时显示的那串）
        for (const m of (GameState.getInstance().player.chatMsgs ?? []).filter(m => m.npcId === this.currentNpcId)) {
            timed.push({ abs: m.abs, entries: [{ type: 'msg', me: m.me, text: m.text, abs: m.abs, ...(m.aff ? { aff: m.aff } : {}) }] });
        }
        timed.sort((a, b) => a.abs - b.abs);
        this.log = timed.flatMap(t => t.entries);
    }

    /** 返回列表页 */
    private closeChat(): void {
        this.currentNpcId = null;
        this.clearChatHint();              // 退出聊天页即无（2026-11-01 用户令），不等 2.2s
        this.chatRoot.classList.add('hidden-chat');
        this.listBlock.classList.remove('hidden-panel');
        this.app.setTabBarVisible(true);
        this.refresh();
    }

    // ===== 详情页（页面 5d · 批次 C） =====

    /** 聊天页信息条 → 详情页（同层覆盖聊天页，返回落回聊天） */
    private openDetail(npcId: string): void {
        this.currentDetailId = npcId;
        this.detailDynExpanded = false;
        this.chatRoot.classList.add('hidden-chat');
        this.managerRoot.classList.add('hidden-chat');
        this.listBlock.classList.add('hidden-panel');
        this.detailRoot.classList.remove('hidden-chat');
        this.app.setTabBarVisible(false);
        this.renderDetail();
    }

    /** 详情页 → 返回：聊天页仍在其下未销毁，直接露出即可 */
    private closeDetail(): void {
        this.currentDetailId = null;
        this.closeDeleteConfirm();
        this.detailRoot.classList.add('hidden-chat');
        if (this.currentNpcId) {
            this.chatRoot.classList.remove('hidden-chat');
            this.renderChatHeads();
            this.renderChatOptions();
            return;
        }
        this.listBlock.classList.remove('hidden-panel');
        this.app.setTabBarVisible(true);
        this.refresh();
    }

    /** 渲染详情：Hero（大头像/姓名/职业·身份/爱心）+ 信息格（随机 NPC：咖位·好感·关系；特殊 NPC：身份·好感·关系）；性格不上屏（用户拍板，只用对话体现） */
    private renderDetail(): void {
        const id = this.currentDetailId;
        if (!id) return;
        const info = this.npcInfo(id);
        if (!info) return;
        const state = GameState.getInstance();
        const favor = state.getFavor(id);
        const t = tierOf(favor);
        const normal = state.player.normalNpcs.find(n => n.id === id);

        // Hero：头像框 5.5rem=110 CSS px，出图统一 master 尺寸由浏览器降采样
        fillAvatarSlot(this.detailAvatar, info.avatar, info.cfg);
        this.detailName.textContent = info.name;
        this.detailSub.textContent = normal ? '演员' : info.role;
        this.detailHearts.innerHTML = '';
        renderHearts(this.detailHearts, favor);
        this.detailDateBadge.textContent = `第${state.player.year + 1}年${state.player.month}月`;

        // 信息格：只放可量化三项（身份/职业由 hero 副字承载，不重复）
        this.detailInfoBlock.innerHTML = '';
        this.detailInfoBlock.appendChild(createSpan('mint-block-title', '个人信息'));
        const cells: [string, string][] = [];
        if (normal) cells.push(['咖位', rankSystem.getRankName(normal.rank, normal.gender)]);
        cells.push(['好感度', `${favor}`]);
        cells.push(['关系', t.name]);
        const row = createDiv('sd-role-info');
        cells.forEach(([k, v]) => {
            const cell = createDiv('sd-role-cell');
            cell.appendChild(createSpan('sd-role-k', k));
            cell.appendChild(createSpan('sd-role-v', v));
            row.appendChild(cell);
        });
        this.detailInfoBlock.appendChild(row);
        // 删除按钮只给**普通随机 NPC**：所有特殊 NPC（竞争型/资源型）一律不出删除键（2026-11-01 用户令）。
        // 判据走 `NormalNpcSystem.isSpecialNpc` 唯一口，⛔ 界面侧不自己遍历 `SPECIAL_NPCS`（第二把尺）。
        const isSpecial = normalNpcSystem.isSpecialNpc(id);
        this.detailDeleteBtn.style.display = (normal && !isSpecial) ? '' : 'none';
        this.renderDynamics(normal);
    }

    /**
     * 近期动向（好友详情页批次）：只读拼三源，⛔ 不新增存档字段、老档零迁移。
     *  - 当前：`activeJobs` 拍摄中 / 在播（置顶，不参与时序排序）
     *  - 时序：`filmography`（播出完结，一作品一条）+ `awardHistory` 中该 NPC 的提名/获奖，按绝对月倒序
     * 无任何数据（新结识 / 资源型特殊 NPC 无生涯数据）⇒ 整块隐藏。
     */
    private renderDynamics(normal?: NormalNpc): void {
        const box = this.detailDynamics;
        box.innerHTML = '';
        if (!normal) { box.style.display = 'none'; return; }
        const state = GameState.getInstance();
        const p = state.player;

        // 当前状态：在拍 / 在播（剧本或角色位查不到就跳过）
        const current: { scriptId: string; roleIndex: number; phase: 'shooting' | 'airing' }[] = [];
        for (const j of normal.activeJobs ?? []) {
            const script = GameData.SCRIPT_MAP[j.scriptId];
            if (!script?.roles[j.roleIndex]) continue;
            current.push({ scriptId: j.scriptId, roleIndex: j.roleIndex, phase: j.phase });
        }

        // 时序：作品（完结月）+ 奖项（颁奖届月），按绝对月倒序
        const timeline: { abs: number; el: HTMLElement }[] = [];
        for (const f of normal.filmography ?? []) {
            timeline.push({ abs: absMonthOf(f.year, f.month), el: dynWorkRow(f) });
        }
        for (const r of p.awardHistory ?? []) {
            if (r.scripted) continue;
            const me = r.nominees.find(n => n.performer === normal.id);
            if (!me) continue;
            timeline.push({ abs: dynCeremonyAbs(r), el: dynAwardRow(r, me, r.nominees[0]?.performer === normal.id) });
        }
        timeline.sort((a, b) => b.abs - a.abs);

        if (!current.length && !timeline.length) { box.style.display = 'none'; return; }
        box.style.display = '';
        box.appendChild(createSpan('mint-block-title', '近期动向'));
        for (const c of current) box.appendChild(dynCurrentRow(c));

        const shown = this.detailDynExpanded ? timeline : timeline.slice(0, DYN_PREVIEW);
        for (const e of shown) box.appendChild(e.el);
        if (timeline.length > DYN_PREVIEW) {
            const open = this.detailDynExpanded;
            const toggle = createSpan('dyn-toggle', open ? '收起 ▴' : `查看全部（${timeline.length}）▾`);
            makeClickable(toggle, () => {
                this.detailDynExpanded = !this.detailDynExpanded;
                this.renderDynamics(normal);
            });
            box.appendChild(toggle);
        }
    }

    // ===== 删除好友确认弹窗（批次 C） =====

    private openDeleteConfirm(): void {
        const id = this.currentDetailId;
        if (!id) return;
        this.delName = socialSystem.getNpcName(id);
        const tip = this.delBody.querySelector('.cancel-tip');
        if (tip) tip.textContent = `确定删除 ${this.delName} 吗？删除后好感清零、移出人脉页，之后跑通告仍可能再次偶遇（重遇即重新初遇）。`;
        this.delOverlay.classList.add('show');
        this.delWrap.classList.add('show');
    }

    private closeDeleteConfirm(): void {
        this.delOverlay?.classList.remove('show');
        this.delWrap?.classList.remove('show');
    }

    private confirmDelete(): void {
        const id = this.currentDetailId;
        this.closeDeleteConfirm();
        if (!id) return;
        // 所有特殊 NPC 不可删（2026-11-01 用户令：删掉等于关掉一个世界事实）。按钮已按 `isSpecialNpc` 隐藏，
        // 这里再拦一道是防别的路径绕过（⛔ 界面侧不自己遍历 `SPECIAL_NPCS`）。
        if (normalNpcSystem.isSpecialNpc(id)) {
            showHint('特殊人脉不能删除');
            return;
        }
        if (!socialSystem.removeFriend(id)) {
            showHint('只能删除已结识的人');
            return;
        }
        // 删除后该 NPC 已未结识：详情/聊天页都不能再留，直接弹回列表
        this.currentDetailId = null;
        this.currentNpcId = null;
        this.detailRoot.classList.add('hidden-chat');
        this.chatRoot.classList.add('hidden-chat');
        this.listBlock.classList.remove('hidden-panel');
        this.app.setTabBarVisible(true);
        EventBus.emit(GameEvents.STATE_CHANGED);
        this.refresh();
        // 删除成功由列表行消失表达，不再弹「已删除 X」
    }

    // ===== 经纪人聊天页（页面 5b） =====

    /** 进入经纪人聊天页（顶栏返回 + 通知历史 + 底部伪输入条；2026-10-02 起解约钮挂清单，⛔ 常驻） */
    private openManagerChat(): void {
        this.currentNpcId = 'MANAGER';
        this.hideAllChatViews();
        this.listBlock.classList.add('hidden-panel');
        this.managerRoot.classList.remove('hidden-chat');
        this.app.setTabBarVisible(false);
        // 进入即已读：标记当前通知数为已读（清除人脉未读红点）
        GameState.getInstance().player.lastReadNoticeCount = GameState.getInstance().player.managerNotices.length;
        EventBus.emit(GameEvents.STATE_CHANGED);
        this.managerOptOpen = true;    // 进页默认摊开底部选项栏（2026-11-01 茄子令，与聊天页一致）
        this.exitManagerBook();
        this.renderManager();
    }

    /** 返回列表页（与普通聊天页共用） */
    private closeManagerChat(): void {
        this.currentNpcId = null;
        this.managerRoot.classList.add('hidden-chat');
        this.managerOptList.classList.remove('show');
        this.managerInputBar.classList.remove('open');
        this.managerOptOpen = false;
        this.exitManagerBook();
        this.listBlock.classList.remove('hidden-panel');
        this.app.setTabBarVisible(true);
        this.refresh();
    }

    /** 模块切换互斥兜底：离开聊天页时隐藏其余覆盖页（正常流程不会同时展开） */
    private hideAllChatViews(): void {
        this.clearChatHint();               // 进详情/经纪人页也算离开聊天页：残影不留
        this.chatRoot.classList.add('hidden-chat');
        this.managerRoot.classList.add('hidden-chat');
        this.detailRoot.classList.add('hidden-chat');
    }

    /** 渲染经纪人页（通知历史 + 清单可用性；2026-10-02 起解约钮挪进清单，重渲染时同步置灰态） */
    private renderManager(): void {
        const state = GameState.getInstance();
        const active = state.player.contracts.filter(c => c.status === 'active');
        this.renderManagerNotices();
        this.renderManagerOptions();
        // 弹窗若开着且合同全部解约，回到空状态
        if (active.length === 0 && this.cancelWrap?.classList.contains('show')) {
            this.closeCancelModal();
        }
    }

    /** 渲染经纪人通知历史（每条一个气泡，最新在下、最老在上，默认滚到底部看最新；空时留白）。
     *  日期行与 NPC 聊天页同款微信形制（换月插一行居中年月，2026-10-25 茄子令补齐——本区原留
     *  「气泡内月注」旧形制，直签对话被实测点名）；空状态默认对话同日删 */
    private renderManagerNotices(): void {
        this.managerNoticeList.innerHTML = '';
        const state = GameState.getInstance();
        const notices = state.player.managerNotices;
        // 最新在下：按入档顺序渲染（managerNotices[0] 最老，push 到末尾的最新）；
        // n.month 即绝对月（year0*12+month1），与 dateDividerEl 的 -1 反推同口径
        let prevAbs: number | null = null;
        notices.forEach(n => {
            if (n.month !== prevAbs) {
                this.managerNoticeList.appendChild(this.dateDividerEl(n.month));
                prevAbs = n.month;
            }
            const bubble = createDiv('bubble npc manager-bubble');
            bubble.appendChild(createSpan(undefined, n.text));
            this.managerNoticeList.appendChild(bubble);
        });
        this.managerNoticeList.scrollTop = this.managerNoticeList.scrollHeight;
    }

    /** 点输入条 = 展开/收起清单（与聊天页 toggleOptions 同款；⛔ 与好友清单共用 state，两页互斥由 hideAllChatViews 保证）。
     *  收起即退出剧本册分支（下次展开回默认清单）。 */
    private toggleManagerOptions(): void {
        this.managerOptOpen = !this.managerOptOpen;
        if (!this.managerOptOpen) this.exitManagerBook();
        this.renderManagerOptions();
    }

    private exitManagerBook(): void {
        this.managerBookMode = false;
        this.managerBookScript = null;
    }

    /** 经纪人页清单：默认四项走聊天页同款方块（当期剧本 → 营销通稿 → 危机公关 → 解约通告，
     *  破坏性垫底），剧本列表/角色列表仍复用 .option-btn chat-opt-row 通栏皮；当期剧本册=清单当导航（2026-10-28
     *  茄子定稿）：点进去选项变剧本列表→点剧本变该剧组可试角色→点角色经纪人开剧本详情页。 */
    private renderManagerOptions(): void {
        // 同聊天页：清单高度变化后保持贴底，否则通知历史最后一条会被裁掉
        const stick = this.isNearBottom(this.managerNoticeList);
        this.renderManagerOptionsBody();
        if (stick) this.managerNoticeList.scrollTop = this.managerNoticeList.scrollHeight;
    }

    private renderManagerOptionsBody(): void {
        this.managerOptList.classList.toggle('no-anim', this.managerOptList.classList.contains('show'));
        this.managerOptList.innerHTML = '';
        this.managerOptList.classList.toggle('show', this.managerOptOpen);
        this.managerInputBar.classList.toggle('open', this.managerOptOpen);
        if (!this.managerOptOpen) return;
        const state = GameState.getInstance();

        const addRow = (label: string, ic: IconName | undefined, dim: boolean, dimHint: string, onClick: () => void): void => {
            const row = createButton(`option-btn chat-opt-row${dim ? ' dim' : ''}`.trim(), label, () => {
                if (dim) { showHint(dimHint); return; }
                // 清单保持展开（与聊天页同款：选完 ⛔ 自动收）；弹窗关掉后仍看得见这条
                onClick();
                this.renderManagerOptions();   // 导航层切换后原地重渲染（addOptRow 同款「每点必整单重渲染」）
            }, ic);
            row.querySelector('span')?.classList.add('opt-text');
            this.managerOptList.appendChild(row);
        };

        // 剧本册分支：两层清单（剧本列表 / 组内角色），详情不关清单；「返回」垫底（2026-10-28 茄子令）。
        // 剧本层=**全量在池剧本**（2026-10-28 茄子令：没有可试角色的剧组也照列——在池名单本身就是
        // 「每月有上新」的信号，滤掉它玩家会以为这几个月都没上新）；角色层只筛可试的，没有就零行。
        if (this.managerBookMode) {
            if (this.managerBookScript == null) {
                scriptSystem.getMarketScripts().forEach(s => {
                    addRow(`《${s.name}》`, undefined, false, '',
                        () => { this.managerBookScript = s.id; });
                });
                addRow('返回', 'chevronLeft', false, '', () => this.exitManagerBook());
            } else {
                const s = GameData.SCRIPT_MAP[this.managerBookScript];
                const roles = s?.roles ?? [];
                const openIdx: number[] = [];
                roles.forEach((role, roleIndex) => {
                    if (scriptSystem.isRoleAuditionable(s!.id, role, roleIndex)) openIdx.push(roleIndex);
                });
                if (openIdx.length === 0) {
                    // 空态一句话（套客串清单 chat-empty 形制；2026-10-28 茄子定稿：不分原因统一这句）
                    this.managerOptList.appendChild(createDiv('chat-empty', ['这个组暂时没有你能试镜的角色。']));
                }
                openIdx.forEach(roleIndex => {
                    const role = roles[roleIndex];
                    // 角色行不带箭头（2026-10-28 茄子抓「‹›并排奇怪」）：›像另有所指、与垫底返回的‹打架，
                    // 无图标的素行才是「这一项就是内容本身」；返回的‹与聊天页邀约清单同款
                    addRow(role.name ? `${role.roleType}「${role.name}」` : role.roleType, undefined, false, '',
                        () => this.openManagerBookDetail(s!.id, roleIndex));
                });
                addRow('返回', 'chevronLeft', false, '', () => { this.managerBookScript = null; });
            }
            return;
        }

        // 默认四项＝一排方块（2026-11-01 茄子令：与聊天页同形制、无图标）；剧本册等长列表仍走通栏整行
        const grid = createDiv('chat-tile-grid');
        this.managerOptList.appendChild(grid);
        const refresh = () => this.renderManagerOptions();
        // 当期剧本恒可点（2026-10-28 茄子令：在池名单=上新信号，⛔ 按可试角色数置灰）
        this.addTile(grid, '当期剧本', null, false, () => { this.managerBookMode = true; }, refresh);
        // 营销通稿/危机公关＝预留位（2026-10-28 茄子令「先预留按钮不实现」：首版弹层太粗糙被撤，
        // 系统层 ManagerDeskSystem+价格表+桩测保留，等 UI 正经设计后再接）
        this.addTile(grid, '营销通稿', null, false, () => showHint('筹备中'), refresh);
        this.addTile(grid, '危机公关', null, false, () => showHint('筹备中'), refresh);

        const active = state.player.contracts.filter(c => c.status === 'active');
        this.addTile(grid, '解约通告', null, active.length === 0, () => this.openCancelModal(), refresh);
    }

    /** 点角色项=经纪人发来剧本详情页：复用直签详情同一份 RoleDetailPop（纯预览无按钮行），
     *  门槛行红绿=试镜结果口径（与试镜周详情同款）：通过绿一直显示/失败锁逐行判/可重试回灰 */
    private openManagerBookDetail(scriptId: string, roleIndex: number): void {
        const s = GameData.SCRIPT_MAP[scriptId];
        if (!s) return;
        const role = s.roles[roleIndex];
        const tried = GameState.getInstance().player.auditionQualified.some(
            q => q.scriptId === scriptId && q.roleIndex === roleIndex);
        const locked = !tried && scriptSystem.isAuditionLocked(scriptId, roleIndex);
        this.directDetail.open(s, role, {
            roleTitle: `试镜角色：${role.roleType}${role.name ? `「${role.name}」` : ''}`,
            reqTone: tried ? 'ok' : (locked ? 'no' : undefined),
        });
    }

    // ===== 解约弹窗 =====

    /** 打开解约弹窗：列出已签约 active 通告（名称 / 剩余周期 / 档期违约线 / 违约金；2026-10-27「作废期限」改「剧级档期」） */
    private openCancelModal(): void {
        const state = GameState.getInstance();
        const active = state.player.contracts.filter(c => c.status === 'active');
        // 置灰方块可点弹原因（M13 口径：⛔ 不开空弹窗）——这里没有计数能表达"零通告"，提示是唯一原因出口
        if (active.length === 0) {
            showHint('暂无已签约通告');
            return;
        }
        this.renderCancelList();
        this.cancelOverlay.classList.add('show');
        this.cancelWrap.classList.add('show');
    }

    private closeCancelModal(): void {
        this.deselectAllCancelItems();
        this.cancelOverlay.classList.remove('show');
        this.cancelWrap.classList.remove('show');
    }

    private deselectAllCancelItems(): void {
        this.cancelList.querySelectorAll('.cancel-item.selected').forEach(el => {
            el.classList.remove('selected');
        });
    }

    /** 渲染通告列表（每项 = 卡片行，选中后按底部 [解约] 执行） */
    private renderCancelList(): void {
        this.cancelList.innerHTML = '';
        const state = GameState.getInstance();
        const active = state.player.contracts.filter(c => c.status === 'active');
        if (active.length === 0) {
            this.cancelList.appendChild(createDiv('npc-empty', ['暂无已签约通告']));
            return;
        }
        const absNow = state.player.year * 12 + state.player.month;
        active.forEach(contract => {
            const script = GameData.SCRIPT_MAP[contract.scriptId];
            if (!script) return;
            // 档期违约线（唯一约束=剧级档期）：走 scheduleDeadlineTextOf（内部 dramaTimelineOf），与违约判定同一把尺
            const deadlineText = scheduleDeadlineTextOf(contract, script, absNow);
            const penalty = scriptSystem.calcPenalty(script, contract.roleIndex);

            const item = makeClickable(createDiv('cancel-item'), () => {
                if (item.classList.contains('selected')) {
                    item.classList.remove('selected');
                } else {
                    this.deselectAllCancelItems();
                    item.classList.add('selected');
                }
            });
            const main = createDiv('cancel-main');
            main.appendChild(createDiv('cancel-item-name', [`《${script.name}》`]));
            main.appendChild(createSpan('cancel-item-meta',
                `还差${scriptSystem.shotWeeksLeftOf(contract)}周 · ${deadlineText}`));
            item.appendChild(main);
            item.appendChild(createSpan('cancel-fee' + (penalty > 0 ? '' : ' fee-free'), penalty > 0 ? `¥${penalty.toLocaleString()}` : '无违约金'));

            item.dataset.contractId = contract.id;
            this.cancelList.appendChild(item);
        });
    }

    /** 确认解约：对选中项执行 scriptSystem.breakContract，成功后刷新弹窗 */
    private confirmBreakContract(): void {
        const selected = this.cancelList.querySelector('.cancel-item.selected') as HTMLElement | null;
        if (!selected?.dataset.contractId) {
            showHint('请先选择要解约的通告');
            return;
        }
        const res = scriptSystem.breakContract(selected.dataset.contractId);
        if (!res.ok) {
            showHint(`解约失败：${res.detail}`);
            return;
        }
        // 解约成功由弹窗列表/通告消失表达；只有扣钱这一笔走数值气泡（无违约金不弹）
        if (res.penalty > 0) showValueChanges([{ label: '违约金', delta: `-¥${res.penalty.toLocaleString()}` }]);
        this.renderCancelList();
        this.renderManager();
        if (this.cancelList.querySelectorAll('.cancel-item').length === 0) {
            this.closeCancelModal();
        }
    }

    // ===== 操作 =====

    /** 聊天：主角开场 → NPC 回复（对话取自 NPC 数据表 chatTexts「主角开场|NPC回复」），好感固定 +2，无选项 */
    private startChat(): void {
        const id = this.currentNpcId;
        if (!id) return;
        if (socialSystem.getRemainingChats() <= 0) return;   // 次数已显示在方块「聊天 N/3」上，⛔ 不再弹提示
        const res = socialSystem.chat(id);
        if (!res.ok) return;

        // 聊天文案解析（格式见 docs/特殊人脉/模板文案格式.md §二.1）：
        // `|` 分说话人（左玩家右 NPC），`//` 分同一方连发气泡；文档口径是每条只写一个来回、
        // 玩家按一次「聊天」就播完，这里仍兼容多段交替；好感 +2 挂 NPC 末气泡（以 NPC 气泡收尾）。
        // 2026-10-14：NPC| 前缀条目已迁至 greetingTexts（招呼系统），chatTexts 永远主角先开口；
        // 保留 NPC| 前缀解析逻辑仅作防御性兜底，正常不应触发。
        const raw = res.text ?? '最近在忙什么呀？|最近怎么样？';
        const npcFirst = raw.startsWith('NPC|');
        const bubbles: { me: boolean; text: string }[] = [];
        (npcFirst ? raw.slice(4) : raw).split('|').forEach((turn, i) => {
            const me = npcFirst ? i % 2 === 1 : i % 2 === 0;
            turn.split('//').forEach(seg => {
                const text = seg.trim();
                if (text) bubbles.push({ me, text });
            });
        });
        const lastNpc = bubbles.map(b => !b.me).lastIndexOf(true);
        const abs = this.nowAbs;
        bubbles.forEach((b, i) =>
            this.log.push({ type: 'msg', me: b.me, text: b.text, abs, ...(i === lastNpc ? { aff: res.gain } : {}) }));
        this.recordChatMsgs(id, bubbles.map((b, i) => ({ me: b.me, text: b.text, ...(i === lastNpc && res.gain ? { aff: res.gain } : {}) })));
        this.renderChat();
        this.renderChatHeads();
        this.renderChatOptions();
        if (res.gain > 0) this.flashChatHint([`好感 +${res.gain}`]);   // +0 不弹（2026-10-27 茄子令：没变动就不显示）
    }

    /** 回应招呼（批次 B）：消费最早一条未回应招呼，主角回话 + NPC 收尾，好感 +1，不占聊天额度 */
    private doRespondGreeting(): void {
        const id = this.currentNpcId;
        if (!id) return;
        const res = greetingSystem.respond(id);
        if (!res.ok) {
            showHint('没有待回应的招呼了');
            return;
        }
        // 玩家回复可能含 // 连发（招呼第二段），拆成多条气泡；好感挂 NPC 首颗
        const meBubbles = res.meText.split('//').map(s => s.trim()).filter(Boolean);
        meBubbles.forEach(b => this.log.push({ type: 'msg', me: true, text: b, abs: this.nowAbs }));
        const npcBubbles = res.npcText.split('//').map(s => s.trim()).filter(Boolean);
        npcBubbles.forEach((b, i) => this.log.push({ type: 'msg', me: false, text: b, abs: this.nowAbs, aff: i === 0 ? res.gain : undefined }));
        this.recordChatMsgs(id, [
            ...meBubbles.map(b => ({ me: true, text: b })),
            ...npcBubbles.map((b, i) => ({ me: false, text: b, aff: i === 0 ? res.gain : undefined })),
        ]);
        this.renderChat();
        this.renderChatHeads();
        this.renderChatOptions();
        EventBus.emit(GameEvents.STATE_CHANGED);
        if (res.gain > 0) this.flashChatHint([`好感 +${res.gain}`]);   // +0 不弹（2026-10-27 茄子令：没变动就不显示）
    }

    private doInvite(): void {
        const id = this.currentNpcId;
        if (!id) return;
        const state = GameState.getInstance();
        const favor = state.getFavor(id);
        if (state.player.socialCounts.invite >= GameConfig.INVITE_LIMIT) return;   // 计数已显示在「邀约 N/1」上，⛔ 不再弹提示
        // 同一 NPC 同时只挂一个（多槽批 2026-10-29）；不同 NPC 可并存、不必等上一场赴完
        const held = (state.player.pendingInvites ?? []).find(i => i.npcId === id);
        if (held) {
            const name = socialSystem.getNpcName(id);
            const wk = held.week;
            showHint(wk
                ? `本月第${['一', '二', '三', '四'][wk - 1] ?? wk}周已与 ${name} 有约，先赴完这场`
                : `已与 ${name} 有约，先去他的聊天页定下周次`);
            return;
        }
        if (favor < 40) {
            showHint(`好感度 ${favor}，需 ≥40 才能邀约`);
            return;
        }
        const res = socialSystem.invite(id);
        if (!res.ok) { showHint(res.reason ?? '邀约未成功'); return; }
        // 统一文案解析（特殊 NPC / 普通 NPC 性格模板）
        const texts = socialSystem.getNpcTexts(id);
        const text = res.success ? texts?.inviteOkText : texts?.inviteFailText;
        if (text) this.log.push({ type: 'msg', me: false, text, abs: this.nowAbs, aff: res.gain });
        this.recordChatMsgs(id, text ? [{ me: false, text }] : []);   // 交换定格记账（账随周收口捎带落盘）
        this.renderChat();
        this.renderChatHeads();
        this.renderChatOptions();
        // 选周自动化：好感在赴约周末自动演出时才结算（婉拒 +0）。成功/婉拒由好友回复气泡承担反馈，⛔ 弹通知（2026-10-28 茄子令）
    }

    // ===== 渲染 =====

    /** 统一 NPC 展示信息解析（特殊 NPC / 普通 NPC 性格模板），找不到返回 null。
     *  cfg = 捏脸立绘配置：普通 NPC 全有（67 批）；固定 NPC 有 avatar 配置的走捏脸
     *  （2026-10-14 起，许林辉首个；见 NPCDef.avatar），没配的维持 emoji（原裁定兜底） */
    private npcInfo(npcId: string): { name: string; role: string; avatar: string; cfg?: AvatarConfig } | null {
        const state = GameState.getInstance();
        const special = GameData.NPC_MAP[npcId];
        if (special) {
            if (special.avatar) prefetchAvatar(special.avatar); // 预热，fillAvatarSlot 时基本零帧命中
            return { name: special.name, role: special.role, avatar: this.avatarIcon(npcId), cfg: special.avatar };
        }
        const normal = state.player.normalNpcs.find(n => n.id === npcId);
        if (normal) {
            const t = PERSONALITY_TEMPLATES[normal.personality as PersonalityKey];
            if (normal.avatar) prefetchAvatar(normal.avatar); // 与特殊 NPC 同口径：渲染前先热缓存
            return { name: normal.name, role: '演员', avatar: t ? t.emoji : '🎭', cfg: normal.avatar };
        }
        return null;
    }

    /** 渲染聊天区（纯气泡流；客串邀请的抉择入口在底部选项清单，⛔ 混在气泡流里） */
    private renderChat(): void {
        this.clearChatHint();              // 重建记录会换掉 DOM，临时气泡的计时器一并清
        this.chatLog.innerHTML = '';
        const name = this.currentNpcId ? socialSystem.getNpcName(this.currentNpcId) : '';
        if (this.log.length === 0) {
            this.chatLog.appendChild(createDiv('chat-empty', [`还没有对话，点下方输入条和 ${name} 聊聊吧`]));
            return;
        }
        // 微信式日期行：与本组上一条不同月就在气泡上方插一行居中年月（2026-10-25 茄子令，只显示年月）。
        // 不带 abs 的条目（组内回话、回复/变动行）跟着组头走，不另插行也不刷新判定。
        let prevAbs: number | null = null;
        this.log.forEach(entry => {
            if (entry.abs !== undefined && entry.abs !== prevAbs) {
                this.chatLog.appendChild(this.dateDividerEl(entry.abs));
                prevAbs = entry.abs;
            }
            this.chatLog.appendChild(this.bubbleEl(entry));
            // 剧情消息的回复：**已答**才在气泡流里补两口气泡（主角那句 + NPC 后续回复）。
            // **未答**不在这里出按钮（2026-10-15 茄子定乙档）——那颗米色胶囊和气泡同形制，实测被读成
            // "玩家已经回复了"；回复入口改走底部待办清单那一行，见 renderChatOptions。
            if (entry.choices?.length && entry.pickedChoice !== undefined) {
                this.chatLog.appendChild(this.bubbleEl({ type: 'msg', me: true, text: entry.choices[entry.pickedChoice] }));
                // NPC 后续回复（reply 字段：玩家选完选项后 NPC 再说一句）
                if (entry.replies?.[entry.pickedChoice]) {
                    this.chatLog.appendChild(this.bubbleEl({ type: 'msg', me: false, text: entry.replies[entry.pickedChoice] }));
                }
            }
            // 这一拍改的账：挂在这段对话最后一颗 NPC 气泡的下面（账落过才出现在这里，见 rebuildChatLog）
            if (entry.changes?.length) {
                const row = createDiv('changes');
                appendChanges(row, entry.changes);
                this.chatLog.appendChild(row);
            }
        });
        this.chatLog.scrollTop = this.chatLog.scrollHeight;
    }

    /** 玩家选定剧情消息回复：落账（这一拍欠着的账 + 所选那条的 flags/effects，口在 `PlotSystem.pickChatReply`）
     *  + 重渲染（气泡流出主角气泡、NPC 回复与变动行，清单里那行消失） */
    private pickPlotChoice(plotChatIdx: number, choiceIdx: number): void {
        const chat = GameState.getInstance().player.plotChats?.[plotChatIdx];
        if (chat) PlotSystem.getInstance().pickChatReply(chat, choiceIdx);
        this.rebuildChatLog();
        this.renderChat();
        this.renderChatOptions();   // 答完要把清单里那一行撤掉（整单原地刷新，不许停在答完前的旧清单）
        this.renderChatHeads();     // 选项可挂 effects，好感/名气读数须跟着走（不许停在答完前的旧值）
        EventBus.emit(GameEvents.STATE_CHANGED);
    }

    /** 玩家选定客串邀请 → 定格落盘（接受即结算蹭名气+好感）→ 清单原地回常态（两口气泡由 rebuildChatLog 回放出来） */
    private resolveInvite(greet: NpcGreeting, idx: number): void {
        const res = greetingSystem.respondInvite(greet, idx);
        if (!res) return;   // 已抉择过（幂等闸），不重复落盘/结算
        this.rebuildChatLog();
        this.renderChat();
        this.renderChatHeads();   // 接受会 +好感，头部爱心/数值须同步（⛔ 停在抉择前的旧值）
        EventBus.emit(GameEvents.STATE_CHANGED);
        if (idx === 0) {
            // 名气 + 好感并列进同一枚流内气泡（2026-11-01 用户令）；+0 段不显示（2026-10-27 茄子令）
            const parts = [
                res.fameGain > 0 ? `名气 +${res.fameGain}` : '',
                res.favorGain > 0 ? `好感 +${res.favorGain}` : '',
            ].filter(Boolean);
            this.flashChatHint(parts);
        }
    }

    /** 月份日期行（微信形制：消息组之间独立居中的一行灰字，2026-10-25 茄子令只显示年月）。
     *  abs 口径＝year0*12+month1（与 plotChats.absMonth 同源），反推须先 -1——
     *  旧 chatFmt 的 floor(abs/12)+1/abs%12+1 缺这层，存量剧情消息月份戳整体错一个月（10-14 起 latent，本批顺手修） */
    private dateDividerEl(abs: number): HTMLElement {
        return createDiv('chat-date', [`第${Math.floor((abs - 1) / 12) + 1}年${(abs - 1) % 12 + 1}月`]);
    }

    /** 气泡元素（⛔ 重复出姓名：聊天页顶栏 chat-npc 已带姓名+头像，2026-10-02 用户口令） */
    private bubbleEl(entry: ChatEntry): HTMLElement {
        const bubble = createDiv(`bubble ${entry.me ? 'me' : 'npc'}`.trim());
        bubble.appendChild(setPlotText(createSpan(), entry.text));   // 剧情私信正文：[[文字]] 与 VN 同一份解析
        // 2026-11-01：气泡内的好感小字（原 .b-aff）已删除，好感变化改走 flashChatHint 的临时通知气泡
        return bubble;
    }

    /** 聊天页头部（NPC 信息条） */
    private renderChatHeads(): void {
        const id = this.currentNpcId;
        if (!id) return;
        const info = this.npcInfo(id);
        if (!info) return;
        const favor = GameState.getInstance().getFavor(id);
        fillAvatarSlot(this.chatAvatar, info.avatar, info.cfg);   // .chat-npc .npc-avatar = 2rem
        this.chatName.textContent = info.name;
        this.chatRole.textContent = info.role;
        this.chatHearts.innerHTML = '';
        renderHearts(this.chatHearts, favor);
        this.chatAffNum.innerHTML = '';
        this.chatAffNum.appendChild(createSpan(undefined, `${favor}`));
        const small = document.createElement('small');
        // 档位词而非「好感度」三字：深浅读档位词（五档），量感读爱心连续填充（44 批）
        small.textContent = tierOf(favor).name;
        this.chatAffNum.appendChild(small);
    }

    /** 输入条伪触发：点任意处展开/收起清单（⛔ 真打字，游戏无自由文本聊天） */
    private toggleOptions(): void {
        this.optOpen = !this.optOpen;
        this.renderChatOptions();
    }

    /**
     * 选项清单重建（9/26 布局改案，形制=用户给图《被囚禁的掌心》）：通栏纯文字行 + 发丝分隔线；
     * 有未办待办时=整单只列待办（客串邀请接受/婉拒、招呼快捷回复、剧情私信的回复选项），答完才露常态清单
     * （9/29 用户改案：待办消息优先，不许与聊天/邀约并存）；都处理完才露常态方块：聊天/邀约/客串/帮宣。
     * 待办之间**并列不互相遮蔽**（2026-10-15 乙档改的这一点）：旧写法是「一条压一条」早退阶梯，那时客串与
     * 招呼按 kind 互斥永不同屏看不出问题，剧情私信是第三本账、会和前两条同屏，再压就把该办的事藏了。
     * 置灰行照旧可点（原生 disabled 会吞点击）：点按 showHint 说原因，⛔ 行增删导致清单跳动。
     */
    private renderChatOptions(): void {
        // 清单高度变化会压缩记录区（默认展开后列表更矮）：渲染前贴底就保持贴底，
        // 否则最后一句会被裁到可视区下方（用户 2026-11-02 实测「被底部选项挡住内容」）。
        const stick = this.isNearBottom(this.chatLog);
        this.renderChatOptionsBody();
        if (stick) this.chatLog.scrollTop = this.chatLog.scrollHeight;
    }

    /** 是否贴在滚动区底部：高度变化后据此决定要不要跟随最新内容（8px 容差＝亚像素/边框） */
    private isNearBottom(el: HTMLElement): boolean {
        return el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    }

    private renderChatOptionsBody(): void {
        // 清单已展开时的重建=动作后原地刷新（addOptRow 每点必整单重渲染），⛔ 再重放入场动画（2026-10-12 用户定甲案，同 5c no-anim 形制）；首次展开照滑入
        this.optList.classList.toggle('no-anim', this.optList.classList.contains('show'));
        this.optList.innerHTML = '';
        this.optList.classList.toggle('show', this.optOpen);
        this.inputBar.classList.toggle('open', this.optOpen);
        if (!this.optOpen || !this.currentNpcId) return;
        if (this.guestInviteMode) { this.renderGuestInviteList(); return; }
        if (this.friendPromoMode) { this.renderFriendPromoList(); return; }
        const state = GameState.getInstance();
        const npcId = this.currentNpcId;
        const favor = state.getFavor(npcId);
        // 四类待办的判据收在 `pendingReplies` 一个口里（进页展不展开也读它，两把尺会分叉）
        const { chatIdx: pendingChatIdx, chat: pendingChat, invite: pendingInvite, greet: pendingG, datePick } =
            this.pendingReplies(npcId);
        if (pendingChat || pendingInvite || pendingG || datePick) {
            pendingChat?.choices?.forEach((text, i) =>
                this.addOptRow(text, false, () => this.pickPlotChoice(pendingChatIdx, i), 'chat'));
            if (datePick) {
                // 赴约选周（选周自动化 2026-10-28）：邀约成功后定本月第几周，到点自动赴约；
                // 周级互斥（2026-10-29 茄子令「一周只能安排一次」）：已过周 / 已被别的行程占用的周都置灰，
                // 置灰行可点弹原因（既有形制）。被定档活动占用（品牌活动/综艺录制）的周在行上直接
                // 显示占用原因（自由日活动呈现批 2026-10-31，替换纯置灰——「有钱没影」的缺口）
                const passedWeeks = Math.max(0, state.player.week - 2);   // 本周=player.week-1（决策日=1，全月可挑）
                const abs = state.player.year * 12 + state.player.month;
                const eventTaken = new Map(endorsementSystem.occupiedEventDetail(abs).map(d => [d.week, d.reason]));
                ['一', '二', '三', '四'].forEach((cn, i) => {
                    const week = i + 1;
                    const passed = i < passedWeeks;
                    const taken = socialSystem.isWeekOccupied(week, npcId);
                    const reason = eventTaken.get(week);
                    this.addOptRow(reason ? `${reason}·第${cn}周` : `第${cn}周`, passed || taken, () => {
                        if (passed) { showHint('这一周已经过了'); return; }
                        if (reason) { showHint(`这一周排了${reason}`); return; }
                        if (taken) { showHint('这一周已经有安排了'); return; }
                        socialSystem.setInviteWeek(npcId, week);
                        this.renderChatOptions();   // 定完回常态清单（界面状态变化即反馈，不弹提示）
                    });
                });
            } else if (pendingInvite) {
                (pendingInvite.inviteOptions ?? ['接受邀请', '婉拒']).forEach((label, idx) =>
                    this.addOptRow(label, false, () => this.resolveInvite(pendingInvite, idx), idx === 0 ? 'check' : 'circleX'));
            } else if (pendingG) {
                // 日常回复选项统一显示「回复」，不写具体语句（2026-11-03 茄子令）；实际发出仍是 roll 时定下的 replyText
                this.addOptRow('回复', false, () => this.doRespondGreeting(), undefined, true);
            }
            return;
        }
        // 常态动作＝一排等宽方块（方案 B″ 2026-11-01 茄子令）：无图标、二字词；待办与二级列表仍走通栏整行
        const grid = createDiv('chat-tile-grid');
        this.optList.appendChild(grid);
        const chats = socialSystem.getRemainingChats();
        this.addTile(grid, '聊天', `${Math.max(0, chats)}/${GameConfig.CHAT_LIMIT}`, chats <= 0, () => {
            if (chats <= 0) return;   // 置灰 + 计数已表达「用完了」，⛔ 不再弹重复提示
            this.startChat();
        });
        // 邀约方块带剩余次数（与聊天同口径：剩余/上限）＋置灰；次数用完/好感不足/已有约都只靠状态表达
        const invitesLeft = Math.max(0, GameConfig.INVITE_LIMIT - state.player.socialCounts.invite);
        this.addTile(grid, '邀约', `${invitesLeft}/${GameConfig.INVITE_LIMIT}`,
            favor < 40 || invitesLeft <= 0 || (state.player.pendingInvites ?? []).some(i => i.npcId === npcId),
            () => this.doInvite());
        // 客串/帮宣（原邀客串/邀宣传）：仅随机 NPC（经纪人/特殊 NPC ⛔ 挂），条件不足=置灰可点弹原因（邀约同款形制）
        if (!GameData.NPC_MAP[this.currentNpcId]) {
            const gate = guestCameoSystem.gate(this.currentNpcId);
            this.addTile(grid, '客串', null, !!gate, () => {
                if (gate) { showHint(gate); return; }
                this.guestInviteMode = true;   // 展开状态由 addTile 尾部统一重渲染保持（清单 ⛔ 收起）
            });
            const promoGate = friendPromoSystem.gate(this.currentNpcId);
            this.addTile(grid, '帮宣', null, !!promoGate, () => {
                if (promoGate) { showHint(promoGate); return; }
                this.friendPromoMode = true;
            });
        }
    }

    /** 常态动作方块（方案 B″）：一排等宽，无图标；主文案 14px/500，计数 12px 灰字做次级。
     *  走 flex 而非固定四列——项数不足（特殊 NPC 两项 / 经纪人侧四项）时自动均分，不留空格。
     *  refresh＝点击后重渲染哪张清单（聊天页 / 经纪人页各传各的，⛔ 写死）。 */
    private addTile(grid: HTMLElement, text: string, count: string | null, dim: boolean,
        onPick: () => void, refresh: () => void = () => this.renderChatOptions()): void {
        const tile = createButton(`chat-tile${dim ? ' dim' : ''}`.trim(), '', () => {
            onPick();
            refresh();
        });
        tile.appendChild(createSpan('tile-text', text));
        if (count) tile.appendChild(createSpan('tile-count', count));
        grid.appendChild(tile);
    }

    /** 「邀客串」二级列表（整单替换，批①同机制）：列出所有结构性合格的戏（active ∧ 一番/二番 ∧ 未撞顶）；
     *  该好友已在此剧客串过 / 已在此剧有戏 → 该行置灰，点按弹原因（2026-09-30 用户改案，⛔ 直接隐藏）；
     *  行=「《剧名》」（⛔ 带角色定位——那是玩家自己的番位，「· 男主角」会被误读成请好友演男主角）；一戏不剩=一行说明；返回=回默认清单 */
    private renderGuestInviteList(): void {
        const rows = guestCameoSystem.contractRows(this.currentNpcId!);
        if (rows.length === 0) {
            this.optList.appendChild(createDiv('chat-empty', [guestCameoSystem.emptyReason(this.currentNpcId!)]));
        }
        rows.forEach(r => {
            // 不走 addOptRow：点剧=当场成交并**原地重开列表**（同月可连请，清单 ⛔ 收起）；置灰行点按只弹原因、⛔ 成交也⛔ 收起
            const row = createButton(`option-btn chat-opt-row${r.disabledReason ? ' dim' : ''}`.trim(),
                `《${r.scriptName}》`,
                () => {
                    if (r.disabledReason) { showHint(r.disabledReason); return; }
                    this.doGuestCameo(r.contractId);
                }, 'check');
            row.querySelector('span')?.classList.add('opt-text');
            this.optList.appendChild(row);
        });
        this.addOptRow('返回', false, () => { this.guestInviteMode = false; }, 'chevronLeft');
    }

    /** 成交一次邀客串：扣好感/抬账本/好友沾光（GuestCameoSystem 原子结算）→ 两口气泡 + 结果行拆报 */
    private doGuestCameo(contractId: string): void {
        const id = this.currentNpcId;
        if (!id) return;
        const res = guestCameoSystem.settle(id, contractId);
        if (!res) {
            showHint('请求未生效：好感不足或状态已变动');
            return;
        }
        this.log.push({ type: 'msg', me: true, text: res.meText, abs: this.nowAbs });
        this.log.push({ type: 'msg', me: false, text: res.npcText, abs: this.nowAbs, aff: -res.favorCost });
        this.recordChatMsgs(id, [{ me: true, text: res.meText }, { me: false, text: res.npcText, aff: -res.favorCost }]);
        this.renderChat();
        this.renderChatHeads();
        EventBus.emit(GameEvents.STATE_CHANGED);
        // 拆报口径（剧宣同款：好感/热度各报各的）；好友沾光名气 ⛔ 上屏（用户口令），仅控制台
        const heatPart = res.heatGain > 0 ? `《${res.scriptName}》热度 +${res.heatGain}` : '';   // +0 段不显示（2026-10-27 茄子令）
        showHint(`好感 −${res.favorCost}${heatPart ? ' ' + heatPart : ''}`);
        console.log(`[客串] ${res.scriptName}：好感-${res.favorCost} 热度+${res.heatGain} ${socialSystem.getNpcName(id)}名气+${res.friendFameGain}`);
        this.guestInviteMode = false;
        this.optOpen = true;   // 成交=持续动作（同月可连请），清单原地回默认继续开着，⛔ 收起
        this.renderChatOptions();
    }

    /** 「邀宣传」二级列表（好友剧宣批 2026-10-28，邀客串同机制）：列出所有结构性合格的戏（已开播 ∧ 宣传窗口内 ∧ 一番/二番 ∧ 未撞顶）；
     *  该好友已在此剧站台过 → 该行置灰，点按弹原因（⛔ 直接隐藏）；一戏不剩=一行说明；返回=回默认清单 */
    private renderFriendPromoList(): void {
        const rows = friendPromoSystem.contractRows(this.currentNpcId!);
        if (rows.length === 0) {
            this.optList.appendChild(createDiv('chat-empty', [friendPromoSystem.emptyReason()]));
        }
        rows.forEach(r => {
            // 不走 addOptRow：点剧=当场成交并**原地重开列表**（同月可连请，清单 ⛔ 收起）；置灰行点按只弹原因
            const row = createButton(`option-btn chat-opt-row${r.disabledReason ? ' dim' : ''}`.trim(),
                `《${r.scriptName}》`,
                () => {
                    if (r.disabledReason) { showHint(r.disabledReason); return; }
                    this.doFriendPromo(r.contractId);
                }, 'check');
            row.querySelector('span')?.classList.add('opt-text');
            this.optList.appendChild(row);
        });
        this.addOptRow('返回', false, () => { this.friendPromoMode = false; }, 'chevronLeft');
    }

    /** 成交一次邀宣传：扣好感/抬账本（FriendPromoSystem 原子结算）→ 两口气泡 + 结果行拆报（⛔ 好友名气无变动） */
    private doFriendPromo(contractId: string): void {
        const id = this.currentNpcId;
        if (!id) return;
        const res = friendPromoSystem.settle(id, contractId);
        if (!res) {
            showHint('请求未生效：好感不足或状态已变动');
            return;
        }
        this.log.push({ type: 'msg', me: true, text: res.meText, abs: this.nowAbs });
        this.log.push({ type: 'msg', me: false, text: res.npcText, abs: this.nowAbs, aff: -res.favorCost });
        this.recordChatMsgs(id, [{ me: true, text: res.meText }, { me: false, text: res.npcText, aff: -res.favorCost }]);
        this.renderChat();
        this.renderChatHeads();
        EventBus.emit(GameEvents.STATE_CHANGED);
        // 拆报口径（客串同款：好感/热度各报各的；+0 段不显示）
        const heatPart = res.heatGain > 0 ? `《${res.scriptName}》热度 +${res.heatGain}` : '';
        showHint(`好感 −${res.favorCost}${heatPart ? ' ' + heatPart : ''}`);
        this.friendPromoMode = false;
        this.optOpen = true;   // 成交=持续动作（同月可连请），清单原地回默认继续开着，⛔ 收起
        this.renderChatOptions();
    }

    /** 单条选项行：复用站内 .option-btn 卡片皮（⛔ 照抄原作裸文字形制，用户口令：只借布局、样式合整体）。
     *  选定后清单**保持展开**（10-03 用户口令：连点 3 次聊天不该先点 3 次输入框）；收起只由「点聊天页别处」或输入条自身负责。
     *  文字 span 必须挂 .opt-text——.option-btn 的墨色/字号规则在该类上，漏了会掉回按钮默认黑（用户实测揪出） */
    private addOptRow(text: string, dim: boolean, onPick: () => void, iconName?: IconName, center = false): void {
        const row = createButton(`option-btn chat-opt-row${dim ? ' dim' : ''}${center ? ' center' : ''}`.trim(), text, () => {
            onPick();
            this.renderChatOptions();
        }, iconName);
        const label = row.querySelector('span');
        if (label) { label.classList.add('opt-text'); setPlotText(label, text); }   // 剧情私信的回复项也认 [[文字]]
        this.optList.appendChild(row);
    }

    refresh(): void {
        const state = GameState.getInstance();

        // 顶部：姓名 + 咖位 + 日期（咖位徽章与微博页一致：只显示咖位名，不带 Lv. 等级）
        this.nameEl.textContent = state.player.name;
        fillAvatarSlot(this.avatarEl, '📇', state.player.avatar);   // 顶栏 .avatar = 3rem
        const rank = state.player.rank;
        fillLevelBadge(this.levelBadge, rankSystem.getRankName(rank));
        this.dateBadge.textContent = `第${state.player.year + 1}年${state.player.month}月`;
        this.chatDateBadge.textContent = this.dateBadge.textContent;
        this.managerDateBadge.textContent = this.dateBadge.textContent;

        this.npcList.innerHTML = '';

        // 置顶经纪人行（页面 5b 入口）：无好感度/关系名，不参与排序
        const managerItem = createDiv('npc-item manager-item');
        const mAvatar = createDiv('npc-avatar manager-avatar');
        fillAvatarSlot(mAvatar, GameData.MANAGER.icon, GameData.MANAGER.avatar);
        managerItem.appendChild(mAvatar);
        const mMain = createDiv('npc-main');
        const mName = createDiv('npc-name', [GameData.MANAGER.name]);
        mName.appendChild(createSpan('npc-role', GameData.MANAGER.role));
        mMain.appendChild(mName);
        managerItem.appendChild(mMain);
        // 未读通知提示：朱红数字胶囊（与底部导航「人脉」未读徽章同款）
        const mRight = createDiv('npc-right npc-right-col');
        const unread = state.player.managerNotices.length - (state.player.lastReadNoticeCount ?? 0);
        if (unread > 0 && state.player.managerNotices.length > 0) {
            mRight.appendChild(createSpan('manager-unread', String(unread)));
        }
        managerItem.appendChild(mRight);
        makeClickable(managerItem, () => this.openManagerChat());
        this.npcList.appendChild(managerItem);

        // NPC 列表：仅已结识（好感 ≥0）——排序档位：有红点 > 特殊人脉 > 普通人脉，同档内按好感度降序
        // 特殊 NPC（竞争型实例）已在 NPCS 主表，用 Set 去重同一 id
        const stateFavor = (id: string): number => state.getFavor(id);
        const unreadOf = (id: string): number =>
            greetingSystem.unreadFor(id) + PlotSystem.getInstance().unreadChats(id);
        const idSet = new Set<string>(GameData.NPCS.map(n => n.id));
        state.player.normalNpcs.forEach(n => idSet.add(n.id));
        const metIds: string[] = [...idSet].filter(id => stateFavor(id) >= 0);
        const groupOf = new Map<string, number>();
        metIds.forEach(id => {
            groupOf.set(id, unreadOf(id) > 0 ? 0 : GameData.NPC_MAP[id] ? 1 : 2);
        });
        metIds.sort((a, b) =>
            (groupOf.get(a) ?? 2) - (groupOf.get(b) ?? 2) || stateFavor(b) - stateFavor(a));
        if (metIds.length === 0) {
            this.npcList.appendChild(createDiv('npc-empty', ['还没有结识任何圈内人脉，多参与通告合作结识新朋友吧']));
            return;
        }
        metIds.forEach(id => {
            const favor = stateFavor(id);
            const info = this.npcInfo(id);
            if (!info) return;
            const t = tierOf(favor);
            const item = createDiv('npc-item');
            const avatarSlot = createDiv('npc-avatar');
            // 头像图装进内层壳：fillAvatarSlot 出图异步清 innerHTML，胶囊若直接挂 slot 会被冲掉
            const avatarImg = createDiv('npc-avatar-img');
            avatarSlot.appendChild(avatarImg);
            fillAvatarSlot(avatarImg, info.avatar, info.cfg);   // 列表行 .npc-avatar = 2rem
            item.appendChild(avatarSlot);
            const main = createDiv('npc-main');
            const nameLine = createDiv('npc-name', [info.name]);
            nameLine.appendChild(createSpan('npc-role', info.role));
            main.appendChild(nameLine);
            item.appendChild(main);
            const right = createDiv('npc-right');
            const heartRow = createDiv('npc-hearts');
            renderHearts(heartRow, favor);
            right.appendChild(heartRow);
            // 一行一枚未读胶囊（2026-10-17 茄子令「按甲落」＝搬回批次B最初那版：挂行的右缘、带数字）：
            // 招呼未读 + 剧情消息未读**合计**，读数口不变（`greetingSystem.unreadFor` ＋ `PlotSystem.unreadChats`），
            // 进聊天页一并清账（`markRead` + `markChatsRead`，见 openChat）。
            // 落位＝与「好感档」同一横排、排在它**左边**（2026-10-19 茄子在两档样张里点乙）：好感档永远贴行右缘，
            // 有消息时是胶囊自己占位，常驻读数一像素不跳。上一版让胶囊贴右缘、把好感档顶左 25 像素，
            // 他问「会不会为气泡挪位置」——会，所以换乙。DOM 顺序＝胶囊在前、好感档在后，实测依据见 ui.css 那条。
            const unreadTotal = unreadOf(id);
            if (unreadTotal > 0) {
                right.appendChild(createSpan('manager-unread npc-unread-line', String(unreadTotal)));
            }
            right.appendChild(createSpan(`npc-rel ${t.cls}`.trim(), `${favor} ${t.name}`));
            item.appendChild(right);
            makeClickable(item, () => this.openChat(id));
            this.npcList.appendChild(item);
        });
    }

    private avatarIcon(npcId: string): string {
        const icons: Record<string, string> = {
            NPC_001: '🎤', NPC_002: '🎬', NPC_003: '🎥', NPC_004: '🎙️',
            NPC_005: '💼', NPC_006: '🎬', NPC_007: '🌍', NPC_008: '🎬',
        };
        return icons[npcId] ?? '🤝';
    }

    destroy(): void {
        // 无外部事件监听
    }
}

// ===== 近期动向行构造（纯函数；口径与「我的实绩」CareerRecordPanel 同源：剧名读履历快照、角色名回表 roleIndex） =====

/** 时序默认收起条数（当前在拍/在播不计入） */
const DYN_PREVIEW = 3;

/** 定位「角色名」；剧本表查不到该位时只留定位（快照仍在，⛔ 编一个角色名） */
function dynRoleLabel(scriptId: string, roleIndex: number, roleType: string, roleName?: string): string {
    // 固定阵容（roleIndex = -1）没有 roles 位，角色名在提名快照里（2026-10-21）
    const role = roleName || GameData.SCRIPT_MAP[scriptId]?.roles[roleIndex]?.name;
    return role ? `${roleType}「${role}」` : roleType;
}

/** 颁奖届的绝对月份：查得到定义按颁奖月算，查不到（届表改过/老档）退化成记录年份的 1 月 */
function dynCeremonyAbs(r: AwardRecord): number {
    const def = findCeremony(r.awardId as AwardId, r.edition);
    return def ? absMonthOf(def.yearIndex, def.month) : absMonthOf(r.year, 1);
}

/** 绝对月 →「第 X 年 · M 月」（absMonthOf 口径：year*12 + month，余 0 = 12 月） */
function dynWhen(abs: number): string {
    const y = Math.floor((abs - 1) / 12);
    return `第${y + 1}年·${abs - y * 12}月`;
}

/** 当前状态一行：剧名 + 拍摄中/在播小旗 + 定位「角色」，置顶、不带日期 */
function dynCurrentRow(c: { scriptId: string; roleIndex: number; phase: 'shooting' | 'airing' }): HTMLElement {
    const script = GameData.SCRIPT_MAP[c.scriptId];
    const role = script.roles[c.roleIndex];
    const item = createDiv('career-item dyn-item');
    item.appendChild(createDiv('career-line', [
        createSpan('career-title', `《${script.name}》`),
        createSpan('career-flag', c.phase === 'airing' ? '在播' : '拍摄中'),
    ]));
    item.appendChild(createDiv('career-line', [
        createSpan('career-sub', dynRoleLabel(c.scriptId, c.roleIndex, role.roleType)),
    ]));
    return item;
}

/** 作品一行：剧名 + 评分，次行定位「角色」+ 播出完结月 */
function dynWorkRow(f: FilmographyEntry): HTMLElement {
    const script = GameData.SCRIPT_MAP[f.scriptId];
    const item = createDiv('career-item dyn-item');
    item.appendChild(createDiv('career-line', [
        createSpan('career-title', `《${f.workName || script?.name || '未知作品'}》`),
        createSpan('career-nums', `评分 ${f.score.toFixed(1)}`),
    ]));
    item.appendChild(createDiv('career-line', [
        createSpan('career-sub', dynRoleLabel(f.scriptId, f.roleIndex, f.roleType)),
        createSpan('career-when', dynWhen(absMonthOf(f.year, f.month))),
    ]));
    return item;
}

/** 奖项一行：届别奖项类别 + 获奖/提名小旗，次行《作品》定位「角色」+ 颁奖月 */
function dynAwardRow(r: AwardRecord, me: AwardNominee, won: boolean): HTMLElement {
    const award = AWARD_MAP[r.awardId as AwardId];
    const item = createDiv('career-item dyn-item');
    item.appendChild(createDiv('career-line', [
        createSpan('career-title',
            `第${r.edition} 届 ${award?.name ?? r.awardId}${award?.categoryNames[r.category] ?? r.category}`),
        createSpan('career-flag', won ? '获奖' : '提名'),
    ]));
    item.appendChild(createDiv('career-line', [
        createSpan('career-sub', `《${me.workName}》${dynRoleLabel(me.scriptId, me.roleIndex, me.roleType, me.roleName)}`),
        createSpan('career-when', dynWhen(dynCeremonyAbs(r))),
    ]));
    return item;
}
